import jwtDecode from "jwt-decode";
import { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import ErrorHandler from "../../../../common/ErrorHandler";
import { errorToast, successToast } from "../../../../react-toastfiy/toast";
import useLOSApplicationId from "../hooks/useLOSApplicationId";
import taService from "../services/ta.service";
import {
  currencyToNumber,
  indianCurrencyFormater,
} from "../../../../helpers/functions/currencyFormater";

const SEGMENTS = [
  {
    key: "tractor",
    label: "Tractor",
    icon: "🚜",
    headerBg: "#FFF9E6",
    headerBorder: "#FFC107",
    headerColor: "#856404",
    inputBorder: "#FFC107",
  },
  {
    key: "car",
    label: "Car",
    icon: "🚗",
    headerBg: "#E8F0FE",
    headerBorder: "#4A90D9",
    headerColor: "#1A4B8C",
    inputBorder: "#4A90D9",
  },
  {
    key: "cv_approved",
    label: "CV Approved",
    icon: "✅",
    headerBg: "#E8F8F0",
    headerBorder: "#28A745",
    headerColor: "#155724",
    inputBorder: "#28A745",
  },
  {
    key: "cv_non_approved",
    label: "CV Non-Approved",
    icon: "⚠️",
    headerBg: "#FFF3E0",
    headerBorder: "#FF9800",
    headerColor: "#7A3C00",
    inputBorder: "#FF9800",
  },
];

const FUNDERS = [
  { key: "TCPL", label: "TYGER" },
  { key: "MT", label: "MT" },
];

const STATE_TABS = [
  {
    key: "approved_state",
    label: "Approved State",
    color: "#198754",
    lightColor: "#E8F8F0",
    icon: "✅",
  },
  {
    key: "non_approved_state",
    label: "Non-Approved State",
    color: "#DC3545",
    lightColor: "#FDECEA",
    icon: "⚠️",
  },
];

const DEFAULT_MARGIN_MATRIX = {
  approved_state: {
    tractor: { TCPL: 20, MT: 30 },
    car: { TCPL: 30, MT: 30 },
    cv_approved: { TCPL: 30, MT: 30 },
    cv_non_approved: { TCPL: 30, MT: 30 },
  },
  non_approved_state: {
    tractor: { TCPL: 30, MT: 30 },
    car: { TCPL: 30, MT: 30 },
    cv_approved: { TCPL: 30, MT: 30 },
    cv_non_approved: { TCPL: 30, MT: 30 },
  },
};

const OpenUpdateFinalLimit = (props) => {
  const { onHide, data } = props;
  const currentUser = localStorage.getItem("refreshToken");
  const user = jwtDecode(currentUser);

  const addMonthsISO = (isoDate, months) => {
    if (!isoDate) return "";
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return "";
    d.setMonth(d.getMonth() + months);
    return d.toISOString().slice(0, 10);
  };

  const initialValue = {
    financierCustomerId: "",
    financierVirtualAccountNo: "",
    agreementDate: data?.agreementDate,
    creditLimit: data?.creditLimit,
    vehicleLimit: "",
    taLimitSetupDate: data?.taLimitSetupDate || "",
    taLimitExpiryDate: data?.taLimitExpiryDate || "",
    marginMatrix: data?.marginMatrix || DEFAULT_MARGIN_MATRIX,
  };

  const [formData, setFormData] = useState(initialValue);
  const [activeStateTab, setActiveStateTab] = useState("approved_state");

  const { setButtonLoader, buttonLoader } = useLOSApplicationId({
    id: data?.taOnboardingCaseId,
    losApplicationId: data?.losApplicationId,
  });

  const getMin = (stateType, segment, funder) =>
    DEFAULT_MARGIN_MATRIX[stateType][segment][funder];

  const isInvalid = (stateType, segment, funder) => {
    const val = formData.marginMatrix[stateType][segment][funder];
    const min = getMin(stateType, segment, funder);
    return val === "" || Number(val) < min || Number(val) > 100;
  };

  const handleMarginChange = (stateType, segment, funder, value) => {
    let parsed = value === "" ? "" : Number(value);
    if (parsed !== "" && parsed > 100) parsed = 100;
    setFormData((prev) => ({
      ...prev,
      marginMatrix: {
        ...prev.marginMatrix,
        [stateType]: {
          ...prev.marginMatrix[stateType],
          [segment]: {
            ...prev.marginMatrix[stateType][segment],
            [funder]: parsed,
          },
        },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all matrix values before proceeding
    for (const stateType of ["approved_state", "non_approved_state"]) {
      for (const seg of SEGMENTS) {
        for (const funder of FUNDERS) {
          if (isInvalid(stateType, seg.key, funder.key)) {
            const min = getMin(stateType, seg.key, funder.key);
            const stateLabel = stateType === "approved_state" ? "Approved State" : "Non-Approved State";
            errorToast(
              `${stateLabel} → ${seg.label} (${funder.label}): must be between ${min}% and 100%`
            );
            setActiveStateTab(stateType);
            return;
          }
        }
      }
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, submit it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      setButtonLoader(true);

      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we update the limit settings.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const body = {
        taOnboardingCaseId: data?.taOnboardingCaseId,
        creditLimit: formData?.creditLimit,
        agreementDate: formData?.agreementDate,
        vehicleLimit: formData?.vehicleLimit,
        financierCustomerId: formData?.financierCustomerId,
        financierVirtualAccountNo: formData?.financierVirtualAccountNo,
        taLimitSetupDate: formData?.taLimitSetupDate,
        taLimitExpiryDate: formData?.taLimitExpiryDate,
        approved_state: formData?.marginMatrix?.approved_state,
        non_approved_state: formData?.marginMatrix?.non_approved_state,
      };

      const response = await taService.UpdateLimitSetting(body);

      Swal.close();
      setButtonLoader(false);

      if (response?.status === 200) {
        successToast(response.msg);
        window.location.reload();
      } else {
        errorToast(response.msg);
      }
    } catch (error) {
      Swal.close();
      setButtonLoader(false);
      console.error(error);
      errorToast(error?.message || "Something went wrong.");
    }
  };

  const handleWheel = (event) => {
    event.target.blur();
  };

  const activeTab = STATE_TABS.find((t) => t.key === activeStateTab);

  try {
    return (
      <Modal
        {...props}
        size="lg"
        backdrop="static"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header
          closeButton
          style={{ borderBottom: "2px solid #e9ecef", padding: "14px 20px" }}
        >
          <Modal.Title>
            <span style={{ fontSize: "1rem", fontWeight: 700, color: "#2c3e50" }}>
              {data?.businessType === "individual" ||
              data?.businessType === "Individual"
                ? `Update TA Limit — ${data?.personName}`
                : `Update TA Limit — ${data?.companyName}`}
            </span>
          </Modal.Title>
        </Modal.Header>

        <form onSubmit={handleSubmit}>
          <Modal.Body style={{ padding: "1.25rem 1.5rem" }}>
            {/* Row 1: Credit Limit + Agreement Date */}
            <div className="row g-3 mb-3">
              <div className="col-12 col-sm-6">
                <label
                  className="form-label mb-1"
                  style={{ fontSize: "0.8rem", fontWeight: 600, color: "#495057" }}
                >
                  TA Credit Limit
                </label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text" style={{ fontSize: "0.85rem" }}>
                    ₹
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    value={indianCurrencyFormater(formData.creditLimit, 0)}
                    placeholder="Enter credit limit"
                    onChange={(e) => {
                      const val = currencyToNumber(e.target.value);
                      setFormData((prev) => ({ ...prev, creditLimit: val }));
                    }}
                    required
                    style={{ fontSize: "0.85rem" }}
                  />
                </div>
              </div>

              <div className="col-12 col-sm-6">
                <label
                  className="form-label mb-1"
                  style={{ fontSize: "0.8rem", fontWeight: 600, color: "#495057" }}
                >
                  TA Agreement Date
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={formData?.agreementDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, agreementDate: e.target.value }))
                  }
                  required
                  style={{ fontSize: "0.85rem" }}
                />
              </div>
            </div>

            {/* Row 1b: TA Limit Setup Date + Expiry Date */}
            <div className="row g-3 mb-3">
              <div className="col-12 col-sm-6">
                <label
                  className="form-label mb-1"
                  style={{ fontSize: "0.8rem", fontWeight: 600, color: "#495057" }}
                >
                  Limit Setup Date
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={formData?.taLimitSetupDate}
                  onChange={(e) => {
                    const setup = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      taLimitSetupDate: setup,
                      taLimitExpiryDate: addMonthsISO(setup, 12),
                    }));
                  }}
                  required
                  style={{ fontSize: "0.85rem" }}
                />
              </div>
              <div className="col-12 col-sm-6">
                <label
                  className="form-label mb-1"
                  style={{ fontSize: "0.8rem", fontWeight: 600, color: "#495057" }}
                >
                  Limit Expiry Date
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={formData?.taLimitExpiryDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, taLimitExpiryDate: e.target.value }))
                  }
                  required
                  style={{ fontSize: "0.85rem" }}
                />
              </div>
            </div>

            {/* Row 2: Vehicle Limit + Financier VA No + Customer ID */}
            <div className="row g-3 mb-3">
              <div className="col-12 col-sm-4">
                <label
                  className="form-label mb-1"
                  style={{ fontSize: "0.8rem", fontWeight: 600, color: "#495057" }}
                >
                  Vehicle Limit
                </label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={formData.vehicleLimit}
                  placeholder="Vehicle limit"
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, vehicleLimit: e.target.value }))
                  }
                  onWheel={handleWheel}
                  required
                  style={{ fontSize: "0.85rem" }}
                />
              </div>

              <div className="col-12 col-sm-4">
                <label
                  className="form-label mb-1"
                  style={{ fontSize: "0.8rem", fontWeight: 600, color: "#495057" }}
                >
                  Financier Virtual A/C
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={formData.financierVirtualAccountNo}
                  placeholder="Virtual account no"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      financierVirtualAccountNo: e.target.value,
                    }))
                  }
                  required
                  style={{ fontSize: "0.85rem" }}
                />
              </div>

              <div className="col-12 col-sm-4">
                <label
                  className="form-label mb-1"
                  style={{ fontSize: "0.8rem", fontWeight: 600, color: "#495057" }}
                >
                  Financier Customer ID
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={formData.financierCustomerId}
                  placeholder="Customer ID"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      financierCustomerId: e.target.value,
                    }))
                  }
                  required
                  style={{ fontSize: "0.85rem" }}
                />
              </div>
            </div>

            {/* Margin Matrix */}
            <div
              style={{
                border: "1.5px solid #dee2e6",
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              {/* Matrix header */}
              <div
                style={{
                  // background: "#FDECEA",
                  padding: "8px 14px",
                  borderBottom: "1px solid #dee2e6",
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                }}
              >
                <span style={{ fontSize: "0.95rem" }}>📊</span>
                <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "#2c3e50" }}>
                  Margin Matrix
                </span>
              </div>

              {/* State type tabs */}
              <div style={{ display: "flex", borderBottom: "1px solid #dee2e6" }}>
                {STATE_TABS.map((tab) => {
                  const isActive = activeStateTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveStateTab(tab.key)}
                      style={{
                        flex: 1,
                        padding: "9px 8px",
                        border: "none",
                        background: isActive ? tab.lightColor : "#fff",
                        color: isActive ? tab.color : "#6c757d",
                        fontWeight: isActive ? 700 : 500,
                        fontSize: "0.78rem",
                        cursor: "pointer",
                        borderBottom: isActive
                          ? `3px solid ${tab.color}`
                          : "3px solid transparent",
                        transition: "all 0.18s",
                        letterSpacing: "0.2px",
                      }}
                    >
                      {tab.icon} {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Matrix grid — horizontally scrollable on small screens */}
              <div style={{ overflowX: "auto", padding: "12px 10px" }}>
                <div style={{ minWidth: "500px" }}>
                  {/* Segment header row */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: "6px",
                      marginBottom: "5px",
                    }}
                  >
                    {SEGMENTS.map((seg) => (
                      <div
                        key={seg.key}
                        style={{
                          background: seg.headerBg,
                          border: `1.5px solid ${seg.headerBorder}`,
                          borderRadius: "7px",
                          padding: "5px 4px",
                          textAlign: "center",
                          fontWeight: 700,
                          fontSize: "0.68rem",
                          color: seg.headerColor,
                          letterSpacing: "0.4px",
                        }}
                      >
                        {seg.icon} {seg.label.toUpperCase()}
                      </div>
                    ))}
                  </div>

                  {/* Funder sub-headers + inputs */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: "6px",
                    }}
                  >
                    {SEGMENTS.map((seg) => (
                      <div
                        key={seg.key}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "4px",
                        }}
                      >
                        {FUNDERS.map((funder) => (
                          <div key={funder.key}>
                            {/* Funder label */}
                            <div
                              style={{
                                background: "#f8f9fa",
                                border: "1px solid #dee2e6",
                                borderBottom: "none",
                                borderRadius: "5px 5px 0 0",
                                padding: "3px 2px",
                                textAlign: "center",
                                fontSize: "0.66rem",
                                fontWeight: 600,
                                color: "#495057",
                              }}
                            >
                              🏦 {funder.label}
                            </div>

                            {/* Number input with % */}
                            {(() => {
                              const invalid = isInvalid(activeStateTab, seg.key, funder.key);
                              const minVal = getMin(activeStateTab, seg.key, funder.key);
                              const currentVal = formData.marginMatrix[activeStateTab][seg.key][funder.key];
                              return (
                                <div>
                                  <div
                                    title={`Min: ${minVal}%`}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      border: `1.5px solid ${invalid ? "#DC3545" : seg.inputBorder}`,
                                      borderRadius: "0 0 5px 5px",
                                      background: invalid ? "#FFF5F5" : "#fff",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <input
                                      type="number"
                                      min={minVal}
                                      max="100"
                                      value={currentVal}
                                      onChange={(e) =>
                                        handleMarginChange(
                                          activeStateTab,
                                          seg.key,
                                          funder.key,
                                          e.target.value
                                        )
                                      }
                                      onWheel={handleWheel}
                                      required
                                      style={{
                                        width: "100%",
                                        border: "none",
                                        outline: "none",
                                        padding: "5px 2px 5px 6px",
                                        fontSize: "0.82rem",
                                        fontWeight: 700,
                                        color: invalid ? "#DC3545" : "#2c3e50",
                                        background: "transparent",
                                      }}
                                    />
                                    <span
                                      style={{
                                        padding: "0 5px 0 1px",
                                        fontSize: "0.75rem",
                                        color: invalid ? "#DC3545" : seg.headerColor,
                                        fontWeight: 700,
                                        flexShrink: 0,
                                      }}
                                    >
                                      %
                                    </span>
                                  </div>
                                  {invalid && (
                                    <div style={{ fontSize: "0.6rem", color: "#DC3545", padding: "2px 3px 0" }}>
                                      Min {minVal}%
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Active state indicator */}
              <div
                style={{
                  padding: "6px 14px",
                  background: activeTab?.lightColor,
                  borderTop: "1px solid #dee2e6",
                  fontSize: "0.72rem",
                  color: activeTab?.color,
                  fontWeight: 600,
                }}
              >
                {activeTab?.icon} Editing margins for:{" "}
                <strong>{activeTab?.label}</strong>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer style={{ borderTop: "2px solid #e9ecef", padding: "10px 20px" }}>
            <Button variant="secondary" size="sm" onClick={onHide}>
              Close
            </Button>
            {buttonLoader ? (
              <button className="btn btn-primary btn-sm" type="button" disabled>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                />
                Loading...
              </button>
            ) : (
              <Button type="submit" size="sm">
                Submit
              </Button>
            )}
          </Modal.Footer>
        </form>
      </Modal>
    );
  } catch (error) {
    return <ErrorHandler error={error} />;
  }
};

export default OpenUpdateFinalLimit;
